/**
 * @file Parent close preflight callable wrappers.
 */

import {
    applyParentCascadingClosureCallable as configuredApplyParentCascadingClosureCallable,
    applyParentCascadingHoldCallable as configuredApplyParentCascadingHoldCallable,
    getParentClosePreflightCallable as configuredGetParentClosePreflightCallable,
    getParentHoldPreflightCallable as configuredGetParentHoldPreflightCallable,
} from "../../app/firebase/callables.js";

/**
 * Creates parent close preflight API helpers.
 * @param {object} deps Dependency bag.
 * @param {Function} deps.applyParentCascadingClosureCallable Apply callable.
 * @param {Function} deps.applyParentCascadingHoldCallable Apply hold callable.
 * @param {Function} deps.getParentClosePreflightCallable Preflight callable.
 * @param {Function} deps.getParentHoldPreflightCallable Hold preflight callable.
 * @returns {object} Parent close preflight API helpers.
 */
export function createParentClosePreflightApi({
    applyParentCascadingClosureCallable,
    applyParentCascadingHoldCallable,
    getParentClosePreflightCallable,
    getParentHoldPreflightCallable,
}) {
    /**
     * Loads backend-owned close preflight data for a parent item.
     * @param {object} payload Callable payload.
     * @returns {Promise<object>} Preflight response data.
     */
    const getParentClosePreflight = async (payload) => {
        const result = await getParentClosePreflightCallable(payload);
        return result.data;
    };

    /**
     * Applies a parent close and optional cascades through the backend.
     * @param {object} payload Callable payload.
     * @returns {Promise<object>} Apply response data.
     */
    const applyParentCascadingClosure = async (payload) => {
        const result = await applyParentCascadingClosureCallable(payload);
        return result.data;
    };

    /**
     * Loads backend-owned on-hold preflight data for a parent item.
     * @param {object} payload Callable payload.
     * @returns {Promise<object>} Preflight response data.
     */
    const getParentHoldPreflight = async (payload) => {
        const result = await getParentHoldPreflightCallable(payload);
        return result.data;
    };

    /**
     * Applies a parent on-hold status and optional cascades through backend.
     * @param {object} payload Callable payload.
     * @returns {Promise<object>} Apply response data.
     */
    const applyParentCascadingHold = async (payload) => {
        const result = await applyParentCascadingHoldCallable(payload);
        return result.data;
    };

    return {
        applyParentCascadingClosure,
        applyParentCascadingHold,
        getParentClosePreflight,
        getParentHoldPreflight,
    };
}

const configuredParentClosePreflightApi = createParentClosePreflightApi({
    applyParentCascadingClosureCallable:
        configuredApplyParentCascadingClosureCallable,
    applyParentCascadingHoldCallable:
        configuredApplyParentCascadingHoldCallable,
    getParentClosePreflightCallable:
        configuredGetParentClosePreflightCallable,
    getParentHoldPreflightCallable:
        configuredGetParentHoldPreflightCallable,
});

export const applyParentCascadingClosure =
    configuredParentClosePreflightApi.applyParentCascadingClosure;
export const applyParentCascadingHold =
    configuredParentClosePreflightApi.applyParentCascadingHold;
export const getParentClosePreflight =
    configuredParentClosePreflightApi.getParentClosePreflight;
export const getParentHoldPreflight =
    configuredParentClosePreflightApi.getParentHoldPreflight;
